import { useRef, useState } from "react";
import * as THREE from "three";
import { ThreeEvent } from "@react-three/fiber";

interface GridElementProps {
  size: number;
  step: number;
  onSegmentClick: (x: number, z: number, rotation: number) => void;
  floor: number;
}

const GridElement: React.FC<GridElementProps> = ({
  size,
  step,
  onSegmentClick,
  floor,
}) => {
  const halfSize = size / 2;
  const [hoveredLine, setHoveredLine] = useState<string | null>(null);
  const segThickness = 0.05;
  const floorHeight = 3;
  const handleClick = (
    event: ThreeEvent<MouseEvent>,
    isHorizontal: boolean,
    x: number,
    z: number
  ) => {
    event.stopPropagation();
    // Use 0 for horizontal lines (north-south walls) and PI/2 for vertical lines (east-west walls)
    const rotation = isHorizontal ? 0 : Math.PI / 2;
    onSegmentClick(x, z, rotation);
  };

  const handlePointerOver = (key: string) => {
    setHoveredLine(key);
  };

  const handlePointerOut = () => {
    setHoveredLine(null);
  };

  // Create grid lines
  const gridLines = [];
  for (let x = -halfSize; x <= halfSize; x += step) {
    for (let z = -halfSize; z <= halfSize; z += step) {
      // Horizontal lines
      const hKey = `h-${x}-${z}`;
      gridLines.push(
        <mesh
          key={hKey}
          position={[x + step / 2, 0, z]}
          rotation={[0, 0, 0]}
          onClick={(e) => handleClick(e, true, x + step / 2, z)}
          onPointerOver={() => handlePointerOver(hKey)}
          onPointerOut={handlePointerOut}
        >
          <boxGeometry args={[step, segThickness, segThickness]} />
          <meshStandardMaterial
            color={hoveredLine === hKey ? "#666666" : "#999999"}
            transparent={hoveredLine === hKey}
            opacity={hoveredLine === hKey ? 0.8 : 0.5}
          />
        </mesh>
      );
      // Vertical lines
      const vKey = `v-${x}-${z}`;
      gridLines.push(
        <mesh
          key={vKey}
          position={[x, 0, z + step / 2]}
          rotation={[0, Math.PI / 2, 0]}
          onClick={(e) => handleClick(e, false, x, z + step / 2)}
          onPointerOver={() => handlePointerOver(vKey)}
          onPointerOut={handlePointerOut}
        >
          <boxGeometry args={[step, segThickness, segThickness]} />
          <meshStandardMaterial
            color={hoveredLine === vKey ? "#666666" : "#999999"}
            transparent={hoveredLine === vKey}
            opacity={hoveredLine === vKey ? 0.8 : 0.2}
          />
        </mesh>
      );
    }
  }

  return (
    <group position={[0, (floor - 1) * floorHeight, 0]}>{gridLines}</group>
  );
};

export default GridElement;
